import { lazy, Suspense } from "react";
import type { ComponentProps } from "react";
import type { BoxMockup3D } from "./BoxMockup3D";

/**
 * Lazy wrapper around `<BoxMockup3D>` so the three.js + react-three-fiber
 * + drei bundle (~520 kB raw / ~135 kB gzipped) is split out of the main
 * chunk. The 3D box is only used on Customise + Approval screens; anyone
 * landing on /, /catalogue, /designs, /orders, etc. should not pay for it.
 *
 * Existing call sites continue to import `BoxMockup` from
 * `@/components/BoxMockup` so this swap is a no-diff change for them.
 */

const BoxMockup3DLazy = lazy(() =>
  import("./BoxMockup3D").then((m) => ({ default: m.BoxMockup3D })),
);

type BoxMockupProps = ComponentProps<typeof BoxMockup3D>;

export function BoxMockup(props: BoxMockupProps) {
  // Match the rendered canvas footprint (540 × 360 px at size=1, scaled by
  // `size`) so the layout doesn't reflow when the lazy chunk loads.
  const size = props.size ?? 1;
  const w = 540 * size;
  const h = 360 * size;
  return (
    <Suspense fallback={<BoxMockupSkeleton width={w} height={h} />}>
      <BoxMockup3DLazy {...props} />
    </Suspense>
  );
}

/**
 * Thin pulse-skeleton matching the preview-stage dimensions. No spinner —
 * a steady placeholder reads as "rendering", a spinner reads as "stuck".
 */
function BoxMockupSkeleton({ width, height }: { width: number; height: number }) {
  return (
    <div
      role="img"
      aria-label="Loading 3D preview"
      style={{ width, height }}
      className="flex items-center justify-center"
    >
      <div
        className="h-[58%] w-[78%] animate-pulse rounded-md bg-bg-2"
        style={{ boxShadow: "inset 0 0 0 1px rgba(33,43,50,0.04)" }}
      />
    </div>
  );
}
